import { Fragment, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  Divider,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import useAccount from 'app/hooks/useAccount';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const Profile = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const { UpdateSecret, GetAccountInfo } = useAccount();

    const user = location.state?.user;

    const [loading, setLoading] = useState(false);
    const [updating, setUpdating] = useState(false);

    const [showPassword, setShowPassword] = useState({
        oldPassword: false,
        newPassword: false,
        confirmPassword: false
    });

    const [account, setAccount] = useState({
        NIK: '',
        EmployeeCode: '',
        EmployeeName: '',
        Department: '',
        CostCenter: '',
        Designation: '',
        Email: '',
        Phone: ''
    });

    const [password, setPassword] = useState({
        oldPassword: '',
        newPassword: '',
        confirmPassword: ''
    });

    useEffect(() => {
        const loadAccount = async () => {
        if (!user?.UserId) return;

        setLoading(true);

        try {
            const result = await GetAccountInfo(user.UserId);

            setAccount(result || {});
        } finally {
            setLoading(false);
        }
        };

        loadAccount();
    }, [user]);

    const handlePasswordChange = (e) => {
        const { name, value } = e.target;

        setPassword((prev) => ({
        ...prev,
        [name]: value
        }));
    };

    const handleBack = () => {
        navigate(-1);
    };

    const handleTogglePassword = (field) => {
        setShowPassword((prev) => ({
            ...prev,
            [field]: !prev[field]
        }));
    };

    const resetPassword = () => {
        setPassword({
            oldPassword: '',
            newPassword: '',
            confirmPassword: ''
        });
    };

    const handleUpdatePassword = async () => {
        if (!password.oldPassword || !password.newPassword || !password.confirmPassword) {
            toast.warning(`All field can't be empty !`);
            return;
        }

        if (password.newPassword !== password.confirmPassword) {
            toast.error('New password not match with confirm password !');
            return;
        }

        try {
            setUpdating(true);

            const payload = {
                employee_code: account?.NIK,
                old_password: password.oldPassword,
                new_password: password.newPassword,
                confirm_password: password.confirmPassword
            };

            const res = await UpdateSecret(payload);

            if (res?.success || res?.status === 200) {
                toast.success('Password has been update successfully !');
                resetPassword();
            } else {
                toast.error(res?.message || 'Failed to update password !');
            }
        } catch (error) {
            toast.error(error?.message || 'Terjadi kesalahan server');
        } finally {
            setUpdating(false);
        }
    };

    if (loading) {
        return (
            <Box
                height="70vh"
                display="flex"
                justifyContent="center"
                alignItems="center"
            >
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Fragment>
        <Box p={3}>
            <Card elevation={3} sx={{ borderRadius: 2 }}>
                <CardContent>

                    <Typography
                        variant="h6"
                        fontWeight={700}
                        mb={3}
                    >
                    ACCOUNT INFORMATION
                    </Typography>

                    <Grid container spacing={2}>

                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            size="small"
                            label="EMPLOYEE CODE"
                            value={account.EmployeeCode}
                            disabled
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            size="small"
                            label="EMPLOYEE NAME"
                            value={account.EmployeeName?.toUpperCase()}
                            disabled
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            size="small"
                            label="COMPANY INFORMATION"
                            value={`${account?.CompanyCode ?? ''}-${account?.CompanyName ?? '-'}`}
                            disabled
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            size="small"
                            label="DEPARTMENT"
                            value={`${account.DepartmentCode ?? ''} - ${account.DepartmentName ?? ''}`}
                            disabled
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            size="small"
                            label="COST CENTRE"
                            value={account?.CostCentreCode ?? '-'}
                            disabled
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            size="small"
                            label="DESIGNATION"
                            value={account?.DesignationCode ?? '-'}
                            disabled
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            size="small"
                            label="EMAIL"
                            value={account?.Email ?? '-'}
                            disabled
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            size="small"
                            label="PHONE NUMBER"
                            value={account?.Phone ?? '-'}
                            disabled
                        />
                    </Grid>

                    </Grid>

                    <Divider sx={{ my: 4 }} />

                    <Typography
                        variant="h6"
                        fontWeight={700}
                        mb={3}
                    >
                    CHANGE PASSWORD
                    </Typography>

                    <Grid container spacing={2}>

                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            size="small"
                            label="OLD PASSWORD"
                            type={showPassword.oldPassword ? 'text' : 'password'}
                            name="oldPassword"
                            value={password.oldPassword}
                            onChange={handlePasswordChange}
                            InputProps={{
                                endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton size='small' onClick={() => handleTogglePassword('oldPassword')}>
                                    {showPassword.oldPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                                )
                            }}
                            />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            size="small"
                            label="NEW PASSWORD"
                            type={showPassword.newPassword ? 'text' : 'password'}
                            name="newPassword"
                            value={password.newPassword}
                            onChange={handlePasswordChange}
                            InputProps={{
                                endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton size='small' onClick={() => handleTogglePassword('newPassword')}>
                                    {showPassword.newPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                                )
                            }}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            size="small"
                            label="CONFIRM PASSWORD"
                            type={showPassword.confirmPassword ? 'text' : 'password'}
                            name="confirmPassword"
                            value={password.confirmPassword}
                            onChange={handlePasswordChange}
                            InputProps={{
                                endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton size='small' onClick={() => handleTogglePassword('confirmPassword')}>
                                    {showPassword.confirmPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                                )
                            }}
                        />
                    </Grid>

                    </Grid>

                    <Box
                        display="flex"
                        justifyContent="flex-end"
                        gap={1}
                        mt={4}
                    >
                        <Button
                            variant="outlined"
                            color="inherit"
                            onClick={handleBack}
                        >
                            BACK
                        </Button>

                        <Button
                            variant="contained"
                            onClick={handleUpdatePassword}
                            disabled={updating}
                        >
                            {updating ? 'UPDATING IN PROGRESS...' : 'UPDATE PASSWORD'}
                        </Button>
                    </Box>
                </CardContent>
            </Card>
        </Box>
        </Fragment>
    );
};

export default Profile;